import React from "react";
import { useState, useEffect } from "react";

// @mui material components
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";

// Vision UI Dashboard PRO React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiInput from "components/VuiInput";

// Custom styles for the Configurator
import ConfiguratorRoot from "components/Configurator/ConfiguratorRoot";

// Vision UI Dashboard PRO React context
import {
  useVisionUIController,
  setOpenConfigurator,
} from "context";
import VuiSnackbar from "../VuiSnackbar";
import SP_API from "../API";
import config from "../../config.json";

function Configurator({userDetails, isShowConfig}) {
  const [controller, dispatch] = useVisionUIController();
  const { openConfigurator } =
    controller;
  const [disabled, setDisabled] = useState(false);
  const [fullName, setFullName] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [APIErrorMessage, setAPIErrorMessage] = useState("");
  const [requestID, setRequestID] = useState("");
  const [APIInfoMessage, setAPIInfoMessage] = useState("");
  const [verifyingDetails, setVerifyingDetails] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");

  // Use the useEffect hook to change the button state for the sidenav type based on window size.
  useEffect(() => {
    // A function that sets the disabled state of the buttons for the sidenav type.
    function handleDisabled() {
      return window.innerWidth > 1200 ? setDisabled(false) : setDisabled(true);
    }

    // The event listener that's calling the handleDisabled function when resizing the window.
    window.addEventListener("resize", handleDisabled);

    // Call the handleDisabled function to set the state with the initial value.
    handleDisabled();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleDisabled);
  }, []);

  const handleCloseConfigurator = () => setOpenConfigurator(dispatch, false);

  const validateEmail = (email) => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  };

  const submitForm = async () => {
    let warningtext;
    if (requestID) {
      // OTP entry now.
      if (!verificationCode || verificationCode.length < 6) {
        warningtext = "Please enter a valid verification code";
      }
    } else {
      if (!phoneNumber || phoneNumber.length != 10) {
        warningtext = "Please enter a valid mobile number";
      }
      if (!emailAddress || !validateEmail(emailAddress)) {
        warningtext = "Please enter a valid e-mail address";
      }
      if (!organizationName || organizationName.length < 3) {
        warningtext = "Please enter your organization name";
      }
      if (!fullName || fullName.length < 4) {
        warningtext = "Please enter your full name";
      }
    }

    if (warningtext) {
      setTimeout(() => {
        setAPIInfoMessage("");
      }, 5000);
      return setAPIInfoMessage(warningtext);
    }

    const additionalInfo = {
      emailAddress, fullName, organizationName
    }

    if (requestID) {
      setVerifyingCode(true);

      SP_API({
        url: config.INDIANFREIGHTINDEX_AUTH,
        method: "POST",
        data: {requestID, verificationCode, additionalInfo}
      }).then((response_json) => {

        window.dataLayer.push({
          user_name: fullName,
          user_org: organizationName,
          user_email: emailAddress,
          user_mobile: phoneNumber,
          event: "freightIndex_user_registration"
        });

        if (response_json.data?.token) {
          localStorage.setItem("token", response_json.data.token);
          // Store user details in localStorage
          const userDetails = {
            name: fullName,
            organization: organizationName,
            email: emailAddress,
            phone: phoneNumber
          };
          localStorage.setItem("userDetails", JSON.stringify(userDetails));
          window.location.reload();
        }
        setVerifyingDetails(false);

      }).catch(err => {
        setVerifyingDetails(false);
        let error_message = "Something went wrong. Please try again.";
        if (err.data) {
          error_message = err.data;
        } else if (err.statusText) {
          error_message = err.statusText;
        }
        setVerifyingCode(false);
        setAPIInfoMessage("");
        setAPIErrorMessage(error_message);
        if (error_message == "Verification code is expired.") {
          setVerifyingDetails(false);
          setRequestID("");
          setVerificationCode("");
        }
      });

    } else {
      setVerifyingDetails(true);

      SP_API({
        url: config.INDIANFREIGHTINDEX_AUTH,
        method: "POST",
        data: {phone: phoneNumber, additionalInfo}
      }).then((response_json) => {
        setVerifyingDetails(false);
        if (response_json.data?.id) {
          setRequestID(response_json.data.id);
        }
        setAPIInfoMessage("Verification code sent to mobile number.");
      }).catch(err => {
        setVerifyingDetails(false);
        let error_message = "Something went wrong. Please try again.";
        if (err.data) {
          error_message = err.data;
        } else if (err.statusText) {
          error_message = err.statusText;
        }
        setAPIErrorMessage(error_message);
      });

    }
  };

  const redirectToSP = async () => {
    const appURL = config.SUPERPROCURE ? config.SUPERPROCURE : "https://app.superprocure.com";
    window.location.href = appURL;
  }

  const logout = async () => {

    SP_API({
      url: config.INDIANFREIGHTINDEX_AUTH,
      method: "POST",
      data: {logout:true}
    }).then((response_json) => {
      localStorage.removeItem("token");
      localStorage.removeItem("userDetails");
      window.location.reload();
    }).catch(err => {
    });
  }

  const fullNameChanged = (input) => {
    // Strip out any non ASCII and trim
    const userInputfullNameChanged = input.target.value.replace(/([^\sa-z]+)/gi,"");
    setFullName(userInputfullNameChanged);
  }

  const verificationCodeChanged = (input) => {
    // Strip out any non digits
    const userInputVerificationCodeChanged = input.target.value.replace(/([^0-9]+)/gi,"").slice(0, 10);
    setVerificationCode(userInputVerificationCodeChanged);
  }

  const organizationNameChanged = (input) => {
    // Strip out any non digits and trim to first 10 digits
    const userInputOrganizationName = input.target.value;
    setOrganizationName(userInputOrganizationName);
  }

  const numberChanged = (input) => {
    // Strip out any non digits and trim to first 10 digits
    const userInputPhoneNumber = input.target.value.replace(/([^0-9]+)/g,"").replace(/^0+/, '').slice(0, 10);
    setPhoneNumber(userInputPhoneNumber);
  }

  const emailAddressChanged = (input) => {
    // Strip out any non digits and trim to first 10 digits
    const userInputEmailAddress = input.target.value.replace(/([^0-9a-z\_\-\.\@]+)/gi,"").trim().toLowerCase();
    setEmailAddress(userInputEmailAddress);
  }

  const toggleSnackBar1 = () => {
    setAPIErrorMessage(!APIErrorMessage);
  }
  const toggleSnackBar2 = () => {
    setAPIInfoMessage(!APIInfoMessage);
  }
  return (
    <>
      <ConfiguratorRoot
        variant="permanent"
        ownerState={{ openConfigurator }}
        className={'dgConfigurator' + (!isShowConfig ? ' show' : '')}
      >
        <VuiBox
          display="flex"
          justifyContent="space-between"
          alignItems="baseline"
          pt={2}
          pb={2}
          px={3}
        >
          {userDetails ? (
            <VuiBox display="flex" flexDirection="column" mt={2} lineHeight={1}>
              <VuiTypography color="test" variant="h5">
                Logged in as
              </VuiTypography>
              <VuiTypography
                variant="lg"
                color="brand"
                mt={2}
                fontWeight="bold"
              >
                {userDetails.name}
              </VuiTypography>
              <VuiTypography variant="button" color="text" fontWeight="regular">
                {userDetails.organization}
              </VuiTypography>
            </VuiBox>
          ) : (
            <VuiBox>
              <VuiTypography
                variant="h5"
                textTransform="capitalize"
                fontWeight="800"
                color="#3b3663"
              >
                Unlock full access
              </VuiTypography>
              <VuiTypography variant="body2" color="text">
                Please fill in your details to unlock all features
              </VuiTypography>
            </VuiBox>
          )}
          <Icon
            sx={({
              typography: { size, fontWeightBold },
              palette: { white },
            }) => ({
              fontSize: `${size.md} !important`,
              fontWeight: `${fontWeightBold} !important`,
              stroke: white.main,
              strokeWidth: '3px',
              cursor: 'pointer',
              mt: 2,
            })}
            onClick={handleCloseConfigurator}
          >
            close
          </Icon>
        </VuiBox>

        <Divider />

        {userDetails ? (
          <>
            {userDetails.isSPUser ? (
              <>
                <VuiBox pb={2} px={3}>
                  <VuiButton
                    variant="contained"
                    size={'large'}
                    color="text"
                    sx={{ py: '8px', fontSize: 'medium' }}
                    fullWidth
                    onClick={redirectToSP}
                  >
                    Go to SuperProcure
                  </VuiButton>
                </VuiBox>
                <Divider />
              </>
            ) : (
              ''
            )}
            <VuiBox pb={2} px={3}>
              <VuiButton
                variant="contained"
                size={'large'}
                color="info"
                sx={{ py: '8px', fontSize: 'medium' }}
                fullWidth
                onClick={logout}
              >
                Logout
              </VuiButton>
            </VuiBox>
          </>
        ) : (
          <>
            <VuiBox pb={2} px={3}>
              <VuiTypography color="text" fontSize={16}>
                Full name *
              </VuiTypography>
              <VuiInput
                placeholder="Your full name"
                value={fullName}
                disabled={verifyingDetails || requestID}
                onChange={fullNameChanged}
              />
            </VuiBox>

            <VuiBox pb={2} px={3}>
              <VuiTypography color="text" fontSize={16}>
                Organization name *
              </VuiTypography>
              <VuiInput
                placeholder="Your organization name"
                value={organizationName}
                disabled={verifyingDetails || requestID}
                onChange={organizationNameChanged}
              />
            </VuiBox>

            <VuiBox pb={2} px={3}>
              <VuiTypography color="text" fontSize={16}>
                E-mail address *
              </VuiTypography>
              <VuiInput
                placeholder="Your e-mail address"
                value={emailAddress}
                disabled={verifyingDetails || requestID}
                onChange={emailAddressChanged}
              />
            </VuiBox>

            <VuiBox pb={2} px={3}>
              <VuiTypography color="text" fontSize={16}>
                Mobile number *
              </VuiTypography>
              <VuiInput
                prefix={'+91'}
                maxlength={10}
                value={phoneNumber}
                onChange={numberChanged}
                disabled={verifyingDetails || requestID}
                placeholder="Your mobile number"
              />
            </VuiBox>

            {requestID ? (
              <VuiBox pb={2} px={3}>
                <VuiTypography color="text" fontSize={16}>
                  Verification code
                </VuiTypography>
                <VuiInput
                  placeholder="Verification code received via SMS"
                  value={verificationCode}
                  disabled={verifyingCode}
                  onChange={verificationCodeChanged}
                />
              </VuiBox>
            ) : (
              ''
            )}

            <VuiBox pb={2} px={3}>
              <VuiButton
                variant="contained"
                size={'large'}
                color="info"
                sx={{ py: '8px', fontSize: 'medium' }}
                fullWidth
                onClick={submitForm}
                disabled={verifyingDetails || verifyingCode}
              >
                {verifyingDetails ? 'Verifying...' : 'Verify'}
              </VuiButton>
            </VuiBox>
          </>
        )}
      </ConfiguratorRoot>
      <VuiSnackbar
        color="error"
        title="Account verification"
        icon="warning"
        content={APIErrorMessage}
        dateTime="just now"
        open={APIErrorMessage}
        close={toggleSnackBar1}
      />

      <VuiSnackbar
        color="info"
        icon="notifications"
        title="Account verification"
        content={APIInfoMessage}
        dateTime="just now"
        open={APIInfoMessage}
        close={toggleSnackBar2}
      />
    </>
  );
}

export default Configurator;
